import { useEffect, useMemo, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import {
  QRLinkCard,
  addStep,
  DEFAULT_QR_PALETTE,
  QRPalette,
} from "./CrearFormularioModal";
import DropDownListCustomized from "../../ui/kit-mdtw/DropDownListCustomized";
import {
  getRecruitmentSources,
  IRecruitmentSourceOption,
} from "../../../services/applicationStatsService";
import { withRecruitmentSource } from "../../../utils/formSourceLink";

// ─── presets ────────────────────────────────────────────────────────────────

const PALETTE_PRESETS: { id: string; label: string; palette: QRPalette }[] = [
  { id: "teamwork", label: "Teamwork", palette: DEFAULT_QR_PALETTE },
  { id: "negro",    label: "Negro",    palette: { start: "#0f172a", end: "#475569" } },
  { id: "azul",     label: "Azul",     palette: { start: "#1e3a8a", end: "#3b82f6" } },
  { id: "verde",    label: "Verde",    palette: { start: "#065f46", end: "#10b981" } },
  { id: "violeta",  label: "Violeta",  palette: { start: "#5b21b6", end: "#a78bfa" } },
  { id: "rojo",     label: "Rojo",     palette: { start: "#991b1b", end: "#ef4444" } },
  { id: "rosa",     label: "Rosa",     palette: { start: "#ec4899", end: "#f9a8d4" } },
  { id: "morado-rosa", label: "Morado a rosa", palette: { start: "#7c3aed", end: "#ec4899" } },
];

const SIZE_PRESETS: { id: string; label: string; hint: string; px: number }[] = [
  { id: "s",  label: "S",  hint: "600 px",  px:  600 },
  { id: "m",  label: "M",  hint: "1200 px", px: 1200 },
  { id: "l",  label: "L",  hint: "2000 px", px: 2000 },
  { id: "xl", label: "XL", hint: "3000 px", px: 3000 },
];

interface IFormularioApi {
  contextFormId: string;
  formType: string;
  client: string;
  businessAreaId: string;
  vacancyId: string;
  recruiterId: string;
  recruiter: string;
  link: string;
  totalRecords: number;
  totalApplications: number;
  steps: number;
  createdAt: string;
}

interface VerEnlacesModalProps {
  formulario: IFormularioApi;
  onClose: () => void;
}

const VerEnlacesModal = ({ formulario, onClose }: VerEnlacesModalProps) => {
  // Esc cierra el modal.
  useEscapeKey(onClose);
  const isDividido = (formulario.steps ?? 1) >= 2;

  const [paletteId, setPaletteId] = useState<string>(PALETTE_PRESETS[0].id);
  const [sizeId, setSizeId]       = useState<string>("xl");
  const [recruitmentSources, setRecruitmentSources] = useState<IRecruitmentSourceOption[]>([]);
  const [sourceFormValues, setSourceFormValues] = useState<any>({ recruitmentSource: null });
  const [sourcesLoading, setSourcesLoading] = useState<boolean>(true);
  const [sourcesError, setSourcesError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setSourcesLoading(true);
    setSourcesError(false);

    getRecruitmentSources()
      .then((sources) => {
        if (cancelled) return;
        setRecruitmentSources(sources.filter((source) => !!source.code?.trim()));
      })
      .catch(() => {
        if (cancelled) return;
        setRecruitmentSources([]);
        setSourcesError(true);
      })
      .finally(() => {
        if (!cancelled) setSourcesLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const selectedSourceCode = sourceFormValues.recruitmentSource?.value?.code ?? "";

  const selectedPalette = useMemo(
    () => PALETTE_PRESETS.find((p) => p.id === paletteId)?.palette ?? DEFAULT_QR_PALETTE,
    [paletteId],
  );
  const selectedExportSize = useMemo(
    () => SIZE_PRESETS.find((s) => s.id === sizeId)?.px ?? 3000,
    [sizeId],
  );

  const links = useMemo(() => {
    if (!formulario.link) return [];
    const built = [withRecruitmentSource(addStep(formulario.link, 1), selectedSourceCode)];
    if (isDividido) {
      built.push(withRecruitmentSource(addStep(formulario.link, 2), selectedSourceCode));
    }
    return built;
  }, [formulario.link, isDividido, selectedSourceCode]);

  const contextLabel = (() => {
    const tipo = (formulario.formType || "").toUpperCase();
    if (tipo === "CLIENTE" && formulario.client) {
      return (
        <>
          Enlaces del formulario para el cliente <strong>{formulario.client}</strong>
        </>
      );
    }
    if (tipo === "CUENTA" && (formulario.businessAreaId || formulario.client)) {
      return (
        <>
          Enlaces del formulario para la cuenta{" "}
          <strong>{formulario.businessAreaId || formulario.client}</strong>
        </>
      );
    }
    if (tipo === "RECLUTADOR") {
      const rName = formulario.recruiter || formulario.recruiterId || "";
      return (
        <>
          Enlaces del formulario para <strong>{rName}</strong>
          {formulario.vacancyId ? (
            <>
              , proceso <strong>{formulario.vacancyId}</strong>
            </>
          ) : null}
        </>
      );
    }
    return <>Enlaces del formulario de postulación</>;
  })();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 18, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: isDividido ? "860px" : "820px",
          borderRadius: "18px",
          overflow: "hidden",
          background: "#fff",
          boxShadow:
            "0 28px 64px rgba(15,23,42,0.28), 0 0 0 1px rgba(15,23,42,0.07)",
        }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
            padding: "22px 24px",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "14px",
              right: "14px",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              lineHeight: 1,
              outline: "none",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color =
                "rgba(255,255,255,0.6)";
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          <div
            style={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              color: "#fff",
            }}
          >
            Enlaces del formulario
          </div>
          <div
            style={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "13px",
              color: "rgba(255,255,255,0.5)",
              marginTop: "3px",
            }}
          >
            {isDividido
              ? "Comparte los siguientes enlaces con los postulantes"
              : "Comparte el siguiente enlace con los postulantes"}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "24px" }}>
          {links.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 8px",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                color: "#94a3b8",
              }}
            >
              Este formulario aún no tiene un enlace disponible.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
                padding: "8px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14.5px",
                  fontWeight: 600,
                  color: "#1e293b",
                  lineHeight: 1.45,
                }}
              >
                {contextLabel}
              </div>

              {/* ── Customization panel ── */}
              <div
                style={{
                  width: "100%",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  textAlign: "left",
                }}
              >
                {/* Fuente de reclutamiento */}
                <div style={{ display: "grid", gridTemplateColumns: "minmax(100px, 120px) minmax(0, 1fr)", alignItems: "end", gap: "10px" }}>
                  <span
                    style={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      paddingBottom: "10px",
                    }}
                  >
                    Fuente
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <DropDownListCustomized
                      list={recruitmentSources.map((source) => ({
                        name: source.name,
                        code: source.code as string,
                      }))}
                      required={false}
                      onValueChange={setSourceFormValues}
                      onPermaValues={sourceFormValues}
                      onPropertyChange="recruitmentSource"
                      placeholder={sourcesLoading ? "Cargando fuentes…" : "Sin fuente específica"}
                      disabled={sourcesLoading || sourcesError}
                    />
                    <div style={{ marginTop: "5px", fontFamily: "Roboto, sans-serif", fontSize: "11px", color: sourcesError ? "#b91c1c" : "#94a3b8" }}>
                      {sourcesError
                        ? "No se pudo cargar el catálogo de fuentes."
                        : selectedSourceCode
                          ? `Se agregará source=${selectedSourceCode} al enlace y al QR.`
                          : "Opcional. Busca y selecciona una fuente para identificar el origen."}
                    </div>
                  </div>
                </div>

                {/* Color */}
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                  <span
                    style={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      minWidth: "120px",
                    }}
                  >
                    Color del QR
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {PALETTE_PRESETS.map((p) => {
                      const active = p.id === paletteId;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setPaletteId(p.id)}
                          title={p.label}
                          aria-label={p.label}
                          style={{
                            position: "relative",
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            border: active ? "2px solid #0065DD" : "2px solid transparent",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            outline: "none",
                            transition: "transform 0.12s",
                            transform: active ? "scale(1.06)" : "scale(1)",
                          }}
                        >
                          <span
                            style={{
                              display: "block",
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              background: `linear-gradient(135deg, ${p.palette.start} 0%, ${p.palette.end} 100%)`,
                              boxShadow: active
                                ? "0 0 0 2px #fff inset, 0 2px 6px rgba(15,23,42,0.18)"
                                : "0 0 0 1px rgba(15,23,42,0.08) inset",
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Size (download only) */}
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                  <span
                    style={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      minWidth: "120px",
                    }}
                  >
                    Tamaño descarga
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {SIZE_PRESETS.map((s) => {
                      const active = s.id === sizeId;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setSizeId(s.id)}
                          style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "5px 12px",
                            borderRadius: "8px",
                            border: active ? "1.5px solid #0065DD" : "1px solid #e2e8f0",
                            background: active ? "rgba(0,101,221,0.08)" : "#fff",
                            color: active ? "#0065DD" : "#475569",
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: active ? 600 : 500,
                            fontSize: "12px",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            lineHeight: 1.15,
                          }}
                        >
                          <span>{s.label}</span>
                          <span style={{ fontSize: "10px", color: active ? "#0065DD" : "#94a3b8", fontWeight: 400 }}>
                            {s.hint}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "11px",
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  El tamaño solo aplica al archivo descargado o copiado del QR.
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  width: "100%",
                  flexDirection: isDividido ? "row" : "column",
                  alignItems: isDividido ? "stretch" : "center",
                }}
              >
                {links.map((url, i) => (
                  <QRLinkCard
                    key={`${url}-${paletteId}`}
                    url={url}
                    stepLabel={isDividido ? `Paso ${i + 1}` : undefined}
                    palette={selectedPalette}
                    exportSize={selectedExportSize}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Footer ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <motion.button
              onClick={onClose}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              style={{
                padding: "8px 22px",
                borderRadius: "9px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cerrar
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VerEnlacesModal;
